/* eslint-disable indent */
"use client";

import { useEffect, useState } from "react";
import { Clock, Heart, HelpCircle, Plus, Search, Wand2, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import LoraCell from "@/components/atoms/LoraCell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { LoraDTO } from "@/types";
interface LoraSelectionModalProps {
  loras: LoraDTO[];
  onLoraSelected: (loras: LoraDTO[]) => void;
  selectedLoras: LoraDTO[];
  handleToggleChange: (checked: boolean) => void;
}
export default function LoraSelectionModal({
  loras,
  onLoraSelected,
  selectedLoras,
  handleToggleChange
}: LoraSelectionModalProps) {
  const [open, setOpen] = useState(false);
  const [selectedLorasState, setSelectedLorasState] = useState<LoraDTO[]>(selectedLoras);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPrivate, setShowPrivate] = useState(false);
  useEffect(() => {
    if (selectedLoras.length > 0 && loras.length > 0 && selectedLoras[0].model !== loras[0]?.model) {
      setSelectedLorasState([]);
    }
  }, [loras, selectedLoras]);
  const togglePublic = (checked: boolean) => {
    setShowPrivate(checked);
    handleToggleChange(checked);
  };
  const filteredLoras = loras.filter(lora => {
    const matchesSearch = lora.loraName.toLowerCase().includes(searchQuery.toLowerCase()) || (lora.shortDescription ?? "").toLowerCase().includes(searchQuery.toLowerCase()) || lora.triggerWord.toLowerCase().includes(searchQuery.toLowerCase());

    // Sort by most recently created first
    return matchesSearch;
  }).sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return 0;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const handleUseSelectedLoras = () => {
    if (selectedLorasState.length > 0) {
      onLoraSelected(selectedLorasState);
      setOpen(false);
    }
  };
  const handleLoraClick = (lora: LoraDTO) => {
    setSelectedLorasState(prev => {
      const index = prev.findIndex(l => l.id === lora.id);
      if (index !== -1) {
        return prev.filter(l => l.id !== lora.id);
      } else if (prev.length < 3) {
        return [...prev, lora];
      }
      return prev;
    });
  };
  const isNewLora = (createdAt: string | undefined) => {
    if (!createdAt) return false;
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return new Date(createdAt) >= oneWeekAgo;
  };
  return <Dialog open={open} onOpenChange={setOpen} data-sentry-element="Dialog" data-sentry-component="LoraSelectionModal" data-sentry-source-file="index.tsx">
            <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="index.tsx">
                <Button className="text-white" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                    <Wand2 className="mr-2 h-5 w-5" data-sentry-element="Wand2" data-sentry-source-file="index.tsx" />
                    Select LORA
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] w-full h-[95vh] p-0 gap-0 bg-background !z-[1002]" data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                <div className="flex flex-col h-full max-h-[95vh]">
                    <DialogHeader className="p-4 border-b border-slate-800" data-sentry-element="DialogHeader" data-sentry-source-file="index.tsx">
                        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="index.tsx">Select a LORA</DialogTitle>
                    </DialogHeader>

                    <div className="flex-shrink-0 p-4 space-y-4 border-b border-slate-800">
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" data-sentry-element="Search" data-sentry-source-file="index.tsx" />
                                <Input placeholder="Search LORAs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 bg-slate-900" data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                            </div>
                            <Popover data-sentry-element="Popover" data-sentry-source-file="index.tsx">
                                <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="index.tsx">
                                    <Button variant="ghost" size="icon" className="shrink-0" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                        <HelpCircle className="h-5 w-5 text-blue-400" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="rounded-xl border border-slate-700 bg-slate-900 p-4 w-[280px]" data-sentry-element="PopoverContent" data-sentry-source-file="index.tsx">
                                    <h3 className="text-lg font-semibold text-white mb-2">
                                        What is a LORA?
                                    </h3>
                                    <p className="text-sm text-gray-300">
                                        A LORA (Low-Rank Adaptation) is a
                                        customized model fine-tuned to create
                                        specific styles, characters, or
                                        subjects.
                                    </p>
                                    <ul className="list-disc pl-4 text-sm text-gray-300 mt-2 space-y-1">
                                        <li>Painting styles</li>
                                        <li>Character designs</li>
                                        <li>Specific objects</li>
                                        <li>Custom environments</li>
                                    </ul>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Switch checked={showPrivate} onCheckedChange={togglePublic} className="data-[state=checked]:bg-secondary" data-sentry-element="Switch" data-sentry-source-file="index.tsx" />
                                <Label className="text-sm font-medium text-gray-300" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                    Show my LORAs
                                </Label>
                            </div>
                            <Link href="/lora/create" className="ml-auto" data-sentry-element="Link" data-sentry-source-file="index.tsx">
                                <Button size="sm" className="!bg-emerald-600 hover:!bg-emerald-700 text-white" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                    <Plus className="h-4 w-4 mr-1" data-sentry-element="Plus" data-sentry-source-file="index.tsx" />
                                    Create LORA
                                </Button>
                            </Link>
                        </div>

                        {selectedLorasState.length > 0 && <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-300">
                                    Selected LORAs ({selectedLorasState.length}
                                    /3):
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedLorasState.map(lora => <Badge key={lora.id} variant="secondary" className="px-2 py-1 text-xs !rounded-full !bg-blue-500 text-white flex items-center gap-1">
                                            {lora.loraName} (
                                            {(lora.weight ?? 1).toFixed(1)})
                                            <Button variant="ghost" size="sm" className="h-4 w-4 p-0 !rounded-full hover:bg-blue-600 text-white ml-1" onClick={() => handleLoraClick(lora)}>
                                                <X className="h-3 w-3" />
                                                <span className="sr-only">
                                                    Remove {lora.loraName}
                                                </span>
                                            </Button>
                                        </Badge>)}
                                </div>
                            </div>}
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <div className="p-4">
                            {filteredLoras.length === 0 ? <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                                    <p className="text-gray-400 mb-4">
                                        No LORAs found. Try adjusting your
                                        search or filters.
                                    </p>
                                    <Link href="/lora/create">
                                        <Button size="sm" className="!bg-emerald-600 hover:!bg-emerald-700 text-white">
                                            <Plus className="h-4 w-4 mr-1" />
                                            Create Your Own LORA
                                        </Button>
                                    </Link>
                                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {filteredLoras.map(lora => {
                const isSelected = selectedLorasState.some(l => l.id === lora.id);
                const selectedLora = isSelected ? selectedLorasState.find(l => l.id === lora.id) : null;
                const currentWeight = selectedLora ? selectedLora.weight ?? 1 : 1;
                return <Card key={lora.id} className={`relative !border-slate-700 transition-all duration-200 hover:shadow-md cursor-pointer !bg-slate-950 ${isSelected ? "ring-2 ring-blue-600 border-none" : ""}`} onClick={() => handleLoraClick(lora)}>
                                                <CardContent className="p-3 flex flex-col h-full">
                                                    <div className="relative h-32 mb-3 flex-shrink-0">
                                                        <Image src={lora.coverImage || "/placeholder.svg"} alt={lora.loraName} layout="fill" objectFit="cover" className="rounded-md" />
                                                    </div>
                                                    <h3 className="font-semibold text-base mb-1 truncate">
                                                        {lora.loraName}
                                                    </h3>
                                                    <p className="text-xs text-gray-400 mb-2 line-clamp-2 flex-grow">
                                                        {lora.shortDescription}
                                                    </p>
                                                    <div className="space-y-2 flex-shrink-0">
                                                        <Badge variant="secondary" className="!bg-slate-800 text-gray-400 !rounded-full text-xs">
                                                            {lora.triggerWord}
                                                        </Badge>
                                                        <div className="flex items-center justify-between text-xs text-gray-400">
                                                            <div className="flex items-center gap-1">
                                                                <Heart className="h-3 w-3 text-blue-500" />
                                                                <span>
                                                                    {lora.likes}
                                                                </span>
                                                            </div>
                                                            {lora.createdAt && <div className="flex items-center gap-1">
                                                                    <Clock className="h-3 w-3" />
                                                                    <span>
                                                                        {new Date(lora.createdAt).toLocaleDateString()}
                                                                    </span>
                                                                </div>}
                                                        </div>
                                                    </div>
                                                    {isNewLora(lora.createdAt) && <Badge className="absolute top-2 left-2 !rounded-full !bg-emerald-600 text-white text-xs">
                                                            New
                                                        </Badge>}
                                                    {isSelected && <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                                                            <LoraCell lora={lora} initialWeight={currentWeight} onFinalWeightChange={(id, finalWeight) => {
                        setSelectedLorasState(prev => prev.map(p => p.id === id ? {
                          ...p,
                          weight: finalWeight
                        } : p));
                      }} />
                                                        </div>}
                                                </CardContent>
                                            </Card>;
              })}
                                </div>}
                        </div>
                    </div>

                    <div className="flex-shrink-0 p-4 border-t border-slate-800">
                        <Button className="w-full !bg-accent hover:!bg-accent/90 text-white" disabled={selectedLorasState.length === 0} onClick={handleUseSelectedLoras} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            Use Selected LORA
                            {selectedLorasState.length !== 1 ? "s" : ""}
                            <span className="ml-1 text-sm">
                                ({selectedLorasState.length}/3)
                            </span>
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>;
}